import {
  useCallback,
  useState,
  type RefObject,
} from "react";

import type { ConversationQuoteDraft } from "@/features/chat/conversationProjection";
import { getMerryMarkdownProjection } from "@/features/chat/markdown";

interface ConversationQuoteSelectionAction {
  draft: ConversationQuoteDraft;
  left: number;
  top: number;
}

function elementFromNode(node: Node | null) {
  if (!node) return null;
  const element = node.nodeType === Node.ELEMENT_NODE
    ? (node as Element)
    : node.parentElement;
  return element instanceof HTMLElement ? element : null;
}

function leafFromBoundary(
  container: Node,
  offset: number,
  edge: "end" | "start",
) {
  const element = elementFromNode(container);
  const containingLeaf = element?.closest<HTMLElement>("[data-md-leaf-id]");
  if (containingLeaf) return { leaf: containingLeaf, useEnd: false };
  if (container.nodeType !== Node.ELEMENT_NODE) return null;
  const childNodes = container.childNodes;
  const preferred = edge === "start" ? childNodes[offset] : childNodes[offset - 1];
  const fallback = edge === "start" ? childNodes[offset - 1] : childNodes[offset];
  const preferredElement = elementFromNode(preferred ?? null);
  const preferredLeaf = preferredElement?.matches("[data-md-leaf-id]")
    ? preferredElement
    : edge === "start"
      ? preferredElement?.querySelector<HTMLElement>("[data-md-leaf-id]")
      : [...(preferredElement?.querySelectorAll<HTMLElement>("[data-md-leaf-id]") ?? [])].at(-1);
  if (preferredLeaf) return { leaf: preferredLeaf, useEnd: edge === "end" };
  const fallbackElement = elementFromNode(fallback ?? null);
  const fallbackLeaves = fallbackElement?.querySelectorAll<HTMLElement>(
    "[data-md-leaf-id]",
  );
  const fallbackLeaf = edge === "start"
    ? [...(fallbackLeaves ?? [])].at(-1)
    : fallbackLeaves?.item(0);
  return fallbackLeaf
    ? { leaf: fallbackLeaf, useEnd: edge === "start" }
    : null;
}

function projectionOffsetFromBoundary(
  container: Node,
  offset: number,
  edge: "end" | "start",
) {
  const boundary = leafFromBoundary(container, offset, edge);
  if (!boundary) return null;
  const start = Number(boundary.leaf.dataset.projectionStart ?? "");
  const end = Number(boundary.leaf.dataset.projectionEnd ?? "");
  if (!Number.isInteger(start) || !Number.isInteger(end) || end < start) {
    return null;
  }
  if (boundary.useEnd) return { leaf: boundary.leaf, offset: end };
  const localPrefix = document.createRange();
  try {
    localPrefix.selectNodeContents(boundary.leaf);
    localPrefix.setEnd(container, offset);
  } catch {
    return { leaf: boundary.leaf, offset: edge === "start" ? start : end };
  }
  const localOffset = localPrefix.toString().length;
  return {
    leaf: boundary.leaf,
    offset: Math.max(start, Math.min(end, start + localOffset)),
  };
}

export function useConversationQuoteSelection(
  rootRef: RefObject<HTMLElement | null>,
) {
  const [action, setAction] = useState<ConversationQuoteSelectionAction | null>(
    null,
  );

  const inspectSelection = useCallback(() => {
    const root = rootRef.current;
    const selection = window.getSelection();
    if (!root || !selection || selection.rangeCount !== 1 || selection.isCollapsed) {
      setAction(null);
      return;
    }
    const range = selection.getRangeAt(0);
    const startElement = elementFromNode(range.startContainer);
    const endElement = elementFromNode(range.endContainer);
    const startRoot = startElement?.closest<HTMLElement>(
      "[data-assistant-selectable]",
    );
    const endRoot = endElement?.closest<HTMLElement>(
      "[data-assistant-selectable]",
    );
    if (!startRoot || startRoot !== endRoot || !root.contains(startRoot)) {
      setAction(null);
      return;
    }

    const messageId = startRoot.dataset.messageId ?? "";
    const contentHash = startRoot.dataset.contentHash ?? "";
    const sequence = Number(startRoot.dataset.sequence ?? "0");
    const startBoundary = projectionOffsetFromBoundary(
      range.startContainer,
      range.startOffset,
      "start",
    );
    const endBoundary = projectionOffsetFromBoundary(
      range.endContainer,
      range.endOffset,
      "end",
    );
    const projectionRoot = startBoundary?.leaf.closest<HTMLElement>(
      "[data-md-projection-root]",
    );
    const endProjectionRoot = endBoundary?.leaf.closest<HTMLElement>(
      "[data-md-projection-root]",
    );
    const projection = projectionRoot
      ? getMerryMarkdownProjection(projectionRoot)
      : null;
    if (
      !messageId ||
      !contentHash ||
      !Number.isInteger(sequence) ||
      sequence <= 0 ||
      !startBoundary ||
      !endBoundary ||
      !projectionRoot ||
      projectionRoot !== endProjectionRoot ||
      !projection ||
      endBoundary.offset <= startBoundary.offset
    ) {
      setAction(null);
      return;
    }
    const text = projection.text.slice(startBoundary.offset, endBoundary.offset);
    if (!text.trim()) {
      setAction(null);
      return;
    }
    const bounds = range.getBoundingClientRect();
    setAction({
      draft: {
        endOffset: endBoundary.offset,
        sourceContentHash: contentHash,
        sourceMessageId: messageId,
        sourceProjection: "rendered_text.v1",
        sourceProjectionText: projection.text,
        sourceSequence: sequence,
        startOffset: startBoundary.offset,
        text,
      },
      left: Math.max(12, Math.min(bounds.left + bounds.width / 2, window.innerWidth - 12)),
      top: Math.max(12, bounds.top - 12),
    });
  }, [rootRef]);

  const handleMouseUp = useCallback(() => inspectSelection(), [inspectSelection]);
  const handleKeyUp = useCallback(() => inspectSelection(), [inspectSelection]);
  const clear = useCallback(() => {
    setAction(null);
    window.getSelection()?.removeAllRanges();
  }, []);

  return { action, clear, handleKeyUp, handleMouseUp };
}
